import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { authService } from '@services/api'
import { useAuthStore } from '@store'
import { ROUTES } from '@constants'
import toast from 'react-hot-toast'

function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [loginMethod, setLoginMethod] = useState('email') // 'email' or 'phone'
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
  })
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const switchLoginMethod = (method) => {
    setLoginMethod(method)
    localStorage.setItem('loginMethod', method)
    window.dispatchEvent(new Event('loginMethodChange'))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (loginMethod === 'phone') {
        // Rediriger vers la page de vérification pour connexion par téléphone
        navigate('/phone-verification')
        return
      }
      
      const loginData = { email: formData.email, password: formData.password }
      const response = await authService.login(loginData)

      // Sauvegarder la préférence "Se souvenir de moi"
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
      } else {
        localStorage.removeItem('rememberMe')
      }

      // Détecter le type: user_type vient du backend; fallback via users_id (admin) vs client_id (client)
      const userType = response.user_type ||
        (response.user?.users_id ? 'admin' : 'client')

      login(response.user, response.token, userType)
      toast.success('Connexion réussie !')

      if (userType === 'admin') {
        localStorage.setItem('admin_token', response.token)
        localStorage.setItem('admin_user', JSON.stringify(response.user))
        navigate('/admin/dashboard')
      } else {
        navigate(ROUTES.LIGDI)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-md mx-auto animate-slide-up">
      <h2 className="text-4xl font-bold text-center mb-2 text-blue-900">Connexion</h2>
      <p className='text-xl font-medium text-center mb-8'>Entrez vos informations ci-dessous <br/> pour vous connecter</p>
      {/* Sélecteur de mode de connexion (Email/Téléphone) masqué temporairement.
          Mode email actif par défaut. Ne pas supprimer : décommenter pour réactiver. */}
      {false && (
        <div className="text-center mb-8">
          <div className="inline-flex rounded-ligdi-md bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => switchLoginMethod('email')}
              className={`px-6 py-2 rounded-ligdi-sm font-medium transition-all ${
                loginMethod === 'email'
                  ? 'bg-secondary text-darkBlue shadow-ligdi-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Adresse Email
            </button>
            <button
              type="button"
              onClick={() => switchLoginMethod('phone')}
              className={`px-6 py-2 rounded-ligdi-sm font-medium transition-all ${
                loginMethod === 'phone'
                  ? 'bg-secondary text-darkBlue shadow-ligdi-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Téléphone
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {loginMethod === 'email' ? (
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Adresse Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Adresse Email"
              className="input-ligdi bg-gray-50"
            />
          </div>
        ) : (
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Numéro téléphonique
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="Numéro téléphonique"
              className="input-ligdi bg-gray-50"
            />
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Mot de passe
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Mot de passe"
              className="input-ligdi bg-gray-50"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
            Se souvenir de moi
          </label>
        </div>

        <button 
          type="submit" 
          className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed text-darkBlue text-xl font-semibold" 
          disabled={loading}
        >
          {loading ? 'Connexion...' : loginMethod === 'email' ? 'Connexion' : 'Recevoir mon code de confirmation'}
        </button>
      </form>

      <div className="mt-6 space-y-4">
        <Link to={ROUTES.FORGOT_PASSWORD} className="w-full block">
          <button type="button" className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-ligdi-md font-medium transition-all text-xl">
            Mot de passe oublié ?
          </button>
        </Link>
        
        <div className="text-center">
          <p className="text-gray-700">
            Pas de compte ?{' '}
            <Link to={ROUTES.REGISTER} className="text-primary font-semibold hover:text-primary-600 underline">
              Créer un ici
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
